import { cva, type VariantProps } from "class-variance-authority";

/**
 * Atelier Button class variants.
 *
 * Shape: pill (rounded-atelier-pill, 999px) — decision locked in
 * atelier-rebrand spec (Q3 Soft Pill).
 *
 * Variants:
 * - primary: CTA ink background, white text (default)
 * - ghost: surface-muted background, ink text
 * - danger: rouge désaturé, white text
 * - subtle: transparent, ink text, hover only
 *
 * Sizes: sm / md (default) / lg
 *
 * Motion rules: transitions on background-color only, 120ms. No spring,
 * no scale, no rotate. See feedback_animations memory.
 */
export const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2",
    "rounded-atelier-pill",
    "font-medium",
    "select-none",
    "transition-colors duration-[120ms] ease-out",
    "atelier-focus-ring",
    "disabled:cursor-not-allowed disabled:opacity-60",
  ].join(" "),
  {
    variants: {
      variant: {
        primary:
          "bg-atelier-brand text-white hover:brightness-95",
        ink:
          "bg-atelier-ink-primary text-atelier-surface-raised hover:brightness-110",
        ghost:
          "bg-atelier-surface-muted text-atelier-ink-primary hover:bg-atelier-surface-muted/75",
        danger: "bg-atelier-danger text-white hover:brightness-95",
        subtle:
          "bg-transparent text-atelier-ink-primary hover:bg-atelier-surface-muted/60",
      },
      size: {
        sm: "h-9 px-4 text-xs",
        md: "h-11 px-5 text-sm",
        lg: "h-12 px-6 text-base",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export type ButtonVariantsProps = VariantProps<typeof buttonVariants>;
